import { useData } from "../../runtime/provider";
import { trpc, RouterOutputs } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import type { z } from "zod";
import { genresPageWireSchema } from "@libs/wire";
import { infiniteDataSchema } from "@utils/durableCache/schemas";

type GenresPage = RouterOutputs["catalog"]["genres"]["listPaginated"];

// One-way drift check: see useForYou for rationale.
type _GenresPageWireSchemaCheck =
  GenresPage extends z.infer<typeof genresPageWireSchema> ? true : never;
const _genresPageWireSchemaDriftCheck: _GenresPageWireSchemaCheck = true;
void _genresPageWireSchemaDriftCheck;

const genresInfiniteSchema = infiniteDataSchema(
  genresPageWireSchema,
  genresPageWireSchema.shape.endCursor,
);

/**
 * Get all genres, or a list of genres
 */
export const useGenres = (limit?: number, options?: any) => {
  const data = useData();
  const input = { limit };
  const queryKey = getQueryKey(
    trpc.catalog.genres.listPaginated,
    input,
    "infinite",
  );
  // NOTE: genres are GLOBAL catalog data — `listPaginated` is a public,
  // territory-scoped procedure with no per-user dimension. Unlike the
  // per-user feeds (useForYou / useRecommended*), this query must NOT be
  // reset/invalidated on `firebaseUser.uid` change. A previous uid-change
  // `reset()` effect raced with the durable-cache `initialData` +
  // `staleTime: 1h` on cold start and could leave the section blank with no
  // refetch (LIS-102 genres-not-loading bug).
  const query = trpc.catalog.genres.listPaginated.useInfiniteQuery(input, {
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
    getNextPageParam: (lastPage: { endCursor?: number }) => {
      return lastPage.endCursor;
    },
    getPreviousPageParam: (firstPage: { startCursor?: number }) => {
      return firstPage.startCursor;
    },
    initialCursor: undefined,
    staleTime: 60 * 60 * 1000, // 1 hour — genres list is essentially static
    ...data.cache.durableInfiniteOptions<GenresPage>(
      queryKey,
      genresInfiniteSchema,
    ),
  });

  return { query };
};
